import { useState, useEffect } from "react";
import { BrowserRouter } from "react-router-dom";
import { useConvexAuth } from "convex/react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { Box, LinearProgress, Typography, Stack } from "@mui/material";
import { AppRoutes } from "./routes";
import { AppShell } from "./components/layout/AppShell";
import { LoginPage } from "./pages/Login";
import { TermsGate } from "./components/TermsGate";
import { useTeamsContext } from "./auth/AuthProvider";

function LoadingScreen({ message }: { message: string }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        bgcolor: "var(--c-bg-base)",
        gap: 3,
      }}
    >
      <Box
        component="img"
        src="/altelix-logo.png"
        alt="Altelix"
        sx={{
          height: 56,
          width: "auto",
          objectFit: "contain",
          filter: "var(--c-logo-filter)",
          animation: "pulse 2s ease-in-out infinite",
          "@keyframes pulse": {
            "0%, 100%": { opacity: 0.75, transform: "scale(1)" },
            "50%":      { opacity: 1,    transform: "scale(1.04)" },
          },
        }}
      />
      <Stack alignItems="center" spacing={1.5}>
        <LinearProgress
          sx={{
            width: 180,
            height: 3,
            borderRadius: 2,
            bgcolor: "rgba(108,92,231,0.1)",
            "& .MuiLinearProgress-bar": {
              background: "linear-gradient(90deg, #6c5ce7, #00cec9)",
            },
          }}
        />
        <Typography variant="body2" sx={{ color: "var(--c-text-secondary)", fontSize: "0.8rem" }}>
          {message}
        </Typography>
      </Stack>
    </Box>
  );
}

/**
 * Auth gate (restored 2026-05-15 / v3.0.66).
 *
 * The revert at commit 7fd2062 ("revert: restore original v3.0.0
 * production frontend bundle") stripped the MSAL gate from this file
 * and reduced App.tsx to BrowserRouter + AppShell. That left the
 * production URL https://altelix-squadfinance.4clouders.com/ open
 * to anonymous visitors — every page rendered without ever checking
 * `useIsAuthenticated()`. main.tsx still wrapped <AuthProvider>, so
 * MSAL was initialized, but no component refused to render until a
 * principal was resolved.
 *
 * The canonical pre-revert gate is restored verbatim from commit
 * 6fdf83f. State machine (top → bottom, first match wins):
 *
 *   1. Teams SDK still initializing       → LoadingScreen
 *   2. MSAL interaction in progress       → LoadingScreen
 *   3. NOT authenticated                  → LoginPage
 *   4. Convex auth still loading          → LoadingScreen (5s grace)
 *   5. Authenticated + Convex ready       → BrowserRouter + AppShell
 *
 * The 5-second Convex grace is intentional: production observed cases
 * where Convex took 3-4s to validate the id_token, and showing the
 * login page during that window made the user click "Sign in" again
 * after they had just authenticated. The grace bridges the gap.
 */
export default function App() {
  const { isAuthenticated: _convexAuth, isLoading: convexLoading } = useConvexAuth();
  const msalAuthenticated = useIsAuthenticated();
  const { inProgress } = useMsal();
  const msalLoading = inProgress !== InteractionStatus.None;
  const { isTeamsInitializing } = useTeamsContext();

  const [convexTimedOut, setConvexTimedOut] = useState(false);
  useEffect(() => {
    if (msalAuthenticated && convexLoading) {
      const timer = setTimeout(() => setConvexTimedOut(true), 5000);
      return () => clearTimeout(timer);
    }
    setConvexTimedOut(false);
  }, [msalAuthenticated, convexLoading]);

  if (isTeamsInitializing) {
    return <LoadingScreen message="Initializing..." />;
  }

  if (msalLoading) {
    return <LoadingScreen message="Signing in..." />;
  }

  if (!msalAuthenticated) {
    return <LoginPage />;
  }

  if (convexLoading && !convexTimedOut) {
    return <LoadingScreen message="Connecting to backend..." />;
  }

  return (
    <BrowserRouter>
      <TermsGate>
        <AppShell>
          <AppRoutes />
        </AppShell>
      </TermsGate>
    </BrowserRouter>
  );
}
